import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  Users, 
  Copy, 
  Star,
  Filter,
  Search,
  Bitcoin,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { useStore } from '../store/useStore'

const Trading = () => {
  const { user } = useStore()
  const [activeTab, setActiveTab] = useState('marketplace')
  const [searchTerm, setSearchTerm] = useState('')

  // Mock trading data
  const topTraders = [
    {
      id: '1',
      username: 'CryptoWhale',
      avatar: '/api/placeholder/40/40',
      roi: 142.5,
      totalPnL: 15.67,
      trades: 89,
      winRate: 78.5,
      followers: 1247,
      isFollowing: false
    },
    {
      id: '2',
      username: 'NFTMaster',
      avatar: '/api/placeholder/40/40',
      roi: 98.3,
      totalPnL: 8.92,
      trades: 156,
      winRate: 82.1,
      followers: 892,
      isFollowing: true
    },
    {
      id: '3',
      username: 'OrdinalsKing',
      avatar: '/api/placeholder/40/40',
      roi: 76.8,
      totalPnL: 12.34,
      trades: 67,
      winRate: 74.6,
      followers: 634,
      isFollowing: false
    }
  ]

  const marketplaceItems = [
    {
      id: '1',
      name: 'Lunatic #1247',
      image: '/api/placeholder/300/300',
      price: 0.0155,
      lastPrice: 0.0140,
      change: 10.7,
      seller: 'CryptoCollector',
      rarity: 'Rare',
      traits: ['Blue Eyes', 'Golden Crown', 'Cosmic Background']
    },
    {
      id: '2',
      name: 'Lunatic #892',
      image: '/api/placeholder/300/300',
      price: 0.0189,
      lastPrice: 0.0175,
      change: 8.0,
      seller: 'NFTHolder',
      rarity: 'Epic',
      traits: ['Red Hair', 'Diamond Hands', 'Nebula Aura']
    },
    {
      id: '3',
      name: 'Lunatic #456',
      image: '/api/placeholder/300/300',
      price: 0.0134,
      lastPrice: 0.0142,
      change: -5.6,
      seller: 'LunaticFan',
      rarity: 'Common',
      traits: ['Green Skin', 'Moon Badge', 'Star Field']
    }
  ]

  const recentTrades = [
    {
      id: '1',
      nft: 'Lunatic #2341',
      price: 0.0167,
      change: 12.3,
      trader: 'CryptoWhale',
      type: 'buy',
      time: '2m ago'
    },
    {
      id: '2',
      nft: 'Lunatic #1789',
      price: 0.0123,
      change: -8.7,
      trader: 'NFTMaster',
      type: 'sell',
      time: '5m ago'
    },
    {
      id: '3',
      nft: 'Lunatic #3456',
      price: 0.0198,
      change: 15.6,
      trader: 'OrdinalsKing',
      type: 'buy',
      time: '12m ago'
    }
  ]

  const handleFollowTrader = (traderId: string) => {
    console.log('Following trader:', traderId)
  }

  const handleCopyTrade = (traderId: string) => {
    console.log('Copying trades from:', traderId)
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Trading</h1>
          <p className="text-dark-400 mt-1">Social trading and NFT marketplace</p>
        </div>
        <div className="flex items-center space-x-4">
          <button className="btn-primary">Create Order</button>
          <button className="btn-secondary">My Portfolio</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2">
        {['marketplace', 'social', 'portfolio'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === tab
                ? 'bg-lunatic-500 text-white'
                : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Marketplace Tab */}
      {activeTab === 'marketplace' && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="card">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search NFTs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-primary pl-10 w-64"
                  />
                </div>
                <select className="input-primary">
                  <option>All Rarities</option>
                  <option>Common</option>
                  <option>Rare</option>
                  <option>Epic</option>
                  <option>Legendary</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-dark-400" />
                <select className="input-primary">
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Recently Listed</option>
                  <option>Ending Soon</option>
                </select>
              </div>
            </div>
          </div>

          {/* NFT Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marketplaceItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card card-hover"
              >
                <div className="relative">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-64 object-cover rounded-lg mb-4"
                  />
                  <div className="absolute top-2 right-2 bg-dark-900/80 backdrop-blur-sm rounded-full px-2 py-1">
                    <span className={`text-xs font-semibold ${
                      item.rarity === 'Legendary' ? 'text-yellow-400' :
                      item.rarity === 'Epic' ? 'text-purple-400' :
                      item.rarity === 'Rare' ? 'text-blue-400' :
                      'text-gray-400'
                    }`}>
                      {item.rarity}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">{item.name}</h3>
                    <span className="text-sm text-dark-400">by {item.seller}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-dark-400">Current Price</p>
                      <p className="text-lg font-semibold text-bitcoin-400">₿ {item.price.toFixed(4)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-dark-400">24h Change</p>
                      <p className={`text-sm font-semibold ${
                        item.change >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {item.change >= 0 ? '+' : ''}{item.change.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {item.traits.slice(0, 2).map(trait => (
                      <span
                        key={trait}
                        className="px-2 py-1 bg-dark-800 text-dark-300 rounded-full text-xs"
                      >
                        {trait}
                      </span>
                    ))}
                    {item.traits.length > 2 && (
                      <span className="px-2 py-1 bg-dark-800 text-dark-300 rounded-full text-xs">
                        +{item.traits.length - 2} more
                      </span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <button className="btn-primary flex-1">Buy Now</button>
                    <button className="btn-secondary px-4">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Social Trading Tab */}
      {activeTab === 'social' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Traders */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">Top Traders</h3>
              <div className="space-y-4">
                {topTraders.map((trader, index) => (
                  <div key={trader.id} className="flex items-center space-x-4 p-4 bg-dark-800/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-lunatic-500/20">
                        <span className="text-sm font-semibold text-lunatic-400">#{index + 1}</span>
                      </div>
                      <img
                        src={trader.avatar}
                        alt={trader.username}
                        className="w-12 h-12 rounded-full"
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-semibold text-white">{trader.username}</span>
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      </div>
                      <p className="text-sm text-dark-400">{trader.followers} followers</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm font-semibold text-green-400">+{trader.roi}%</p>
                        <p className="text-xs text-dark-400">ROI</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{trader.winRate}%</p>
                        <p className="text-xs text-dark-400">Win Rate</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleFollowTrader(trader.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          trader.isFollowing
                            ? 'bg-lunatic-500 text-white'
                            : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                        }`}
                      >
                        {trader.isFollowing ? 'Following' : 'Follow'}
                      </button>
                      <button
                        onClick={() => handleCopyTrade(trader.id)}
                        className="btn-secondary px-4 py-2 flex items-center space-x-2"
                      >
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Trades */}
          <div className="space-y-6">
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Trades</h3>
              <div className="space-y-3">
                {recentTrades.map(trade => (
                  <div key={trade.id} className="flex items-center space-x-3 p-3 bg-dark-800/50 rounded-lg">
                    <div className={`p-2 rounded-full ${
                      trade.type === 'buy' ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {trade.type === 'buy' ? (
                        <ArrowUpRight className="w-4 h-4 text-green-400" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{trade.nft}</p>
                      <p className="text-xs text-dark-400">{trade.trader} • {trade.time}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-white">₿ {trade.price.toFixed(4)}</p>
                      <p className={`text-xs ${
                        trade.change >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {trade.change >= 0 ? '+' : ''}{trade.change.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Tab */}
      {activeTab === 'portfolio' && (
        <div className="card">
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-dark-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Connect Your Wallet</h3>
            <p className="text-dark-400 mb-6">Connect your wallet to view your portfolio and start trading</p>
            <button className="btn-primary">Connect Wallet</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Trading 